import { useEffect, useRef } from 'react';

/**
 * setInterval that only ticks while the app is actually on screen.
 *
 * Countdowns, live tables and pending-tap pollers all kept firing while the Mini
 * App sat in the background. Nobody could see the result, but every wake-up
 * still costs the phone — and a handful of them together is what turns into
 * warmth in the player's hand over a long session.
 *
 * The callback fires once more the moment the app comes back, so anything that
 * derives its value from the clock (a match timer, a season countdown) snaps
 * straight to the truth instead of resuming from a stale frame.
 */
export function useVisibleInterval(callback, delayMs) {
  const saved = useRef(callback);
  useEffect(() => {
    saved.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delayMs == null) return undefined;

    let id = 0;
    const run = () => saved.current?.();

    const start = () => {
      if (id) return;
      id = window.setInterval(run, delayMs);
    };
    const stop = () => {
      if (!id) return;
      window.clearInterval(id);
      id = 0;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        run();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [delayMs]);
}
